import { computed, toValue, type MaybeRefOrGetter } from "vue"
import { useQuery } from "@tanstack/vue-query"
import {
  PlanYearEnrollmentElectionStatusEnum,
  type BenefitCoverage,
  type ConsumerMembership,
  type DigitalBenefitCard,
  type MemberEnrollment
} from "@vitable/asclepius-api-client-ts"
import useAsclepius from "@common/composables/api/asclepius"
import { retryNetworkAndServerErrors } from "@/utils/query-retry"

const STALE_TIME_MS = 5 * 60 * 1000

export const memberBenefitsKeys = {
  all: ["member-benefits"] as const,
  coverage: (memberId: string) => [...memberBenefitsKeys.all, "coverage", memberId] as const,
  enrollments: (memberId: string) => [...memberBenefitsKeys.all, "enrollments", memberId] as const,
  idCards: (memberId: string) => [...memberBenefitsKeys.all, "id-cards", memberId] as const,
  consumerMembership: (memberId: string) => [...memberBenefitsKeys.all, "consumer-membership", memberId] as const
}

/** `memberId` is the prefixed public id (`mbr_…`); the query waits until it is known. */
export function useMemberBenefitCoverage(memberId: MaybeRefOrGetter<string | undefined>) {
  const { getMembersApi } = useAsclepius()

  const query = useQuery({
    queryKey: computed(() => memberBenefitsKeys.coverage(toValue(memberId) ?? "")),
    enabled: computed(() => Boolean(toValue(memberId))),
    queryFn: async ({ queryKey }): Promise<Array<BenefitCoverage>> => {
      const response = await getMembersApi().listMemberBenefitCoverage({ memberId: queryKey[2] })
      return response.data.data
    },
    staleTime: STALE_TIME_MS,
    retry: retryNetworkAndServerErrors
  })

  const coverages = computed<Array<BenefitCoverage>>(() => query.data.value ?? [])

  return { ...query, coverages }
}

/** Ids (`enrl_…`) of the enrollments the member can still answer, scoped to the caller's organization. */
export function usePendingMemberEnrollmentIds(memberId: MaybeRefOrGetter<string | undefined>) {
  const { getMembersApi } = useAsclepius()

  const query = useQuery({
    queryKey: computed(() => memberBenefitsKeys.enrollments(toValue(memberId) ?? "")),
    enabled: computed(() => Boolean(toValue(memberId))),
    queryFn: async ({ queryKey }): Promise<Array<MemberEnrollment>> => {
      const response = await getMembersApi().listMemberEnrollments({ memberId: queryKey[2] })
      return response.data.data
    },
    staleTime: STALE_TIME_MS,
    retry: retryNetworkAndServerErrors
  })

  const pendingEnrollmentIds = computed<ReadonlySet<string>>(
    () =>
      new Set(
        (query.data.value ?? [])
          .filter((enrollment) => enrollment.election_status === PlanYearEnrollmentElectionStatusEnum.Pending)
          .map((enrollment) => enrollment.id)
      )
  )

  return { ...query, pendingEnrollmentIds }
}

/** Medical, dental, and vision cards for plans that issue a digital card, plus the VitableRx card. */
export function useMemberIdCards(memberId: MaybeRefOrGetter<string | undefined>) {
  const { getMembersApi } = useAsclepius()

  const query = useQuery({
    queryKey: computed(() => memberBenefitsKeys.idCards(toValue(memberId) ?? "")),
    enabled: computed(() => Boolean(toValue(memberId))),
    queryFn: async ({ queryKey }): Promise<Array<DigitalBenefitCard>> => {
      const response = await getMembersApi().listMemberIdCards({ memberId: queryKey[2] })
      return response.data.data
    },
    staleTime: STALE_TIME_MS,
    retry: retryNetworkAndServerErrors
  })

  const cards = computed<Array<DigitalBenefitCard>>(() => query.data.value ?? [])

  return { ...query, cards }
}

/** The member's own Vitable membership or the one they inherit from their head of household; null when neither. */
export function useMemberConsumerMembership(memberId: MaybeRefOrGetter<string | undefined>) {
  const { getMembersApi } = useAsclepius()

  const query = useQuery({
    queryKey: computed(() => memberBenefitsKeys.consumerMembership(toValue(memberId) ?? "")),
    enabled: computed(() => Boolean(toValue(memberId))),
    queryFn: async ({ queryKey }): Promise<ConsumerMembership | null> => {
      const response = await getMembersApi().getMemberConsumerMembership({ memberId: queryKey[2] })
      return response.data.data
    },
    staleTime: STALE_TIME_MS,
    retry: retryNetworkAndServerErrors
  })

  const membership = computed<ConsumerMembership | null>(() => query.data.value ?? null)

  return { ...query, membership }
}
